import { useEffect, useMemo, useState, type ReactNode } from 'react'
import PalletIdText from '@/components/PalletIdText'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { POLL } from '@/lib/pollTiers'
import { Link, useSearchParams } from 'react-router-dom'
import { paramsPatch } from '@/lib/urlParams'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { transferBinsError, assertTransferDestination, transferSourceLabel, invalidateColdroomViews } from './coldroomTransferErrors'
import CellPicker from '@/components/CellPicker'
import PageHeader from '@/components/PageHeader'
import QueryStateGate from '@/components/QueryStateGate'
import Modal from '@/components/Modal'
import { SplitPane, DetailShell } from '@/components/SplitPane'
import {
  Snowflake, Package, AlertTriangle, FileText, Layers, ChevronDown, ChevronRight,
} from 'lucide-react'
import clsx from 'clsx'
import { supabase } from '@/lib/supabase'
import { cellMark } from './coldroomCellMark'
import { getAgeStatus, effectiveAgeStart, isExportPallet, type AgeBand } from '@/lib/aging'
import { useProductionFlow } from '@/lib/useProductionFlow'
import { computeColdroomCapacity } from '@/lib/coldroomCapacity'
import {
  locationKind, isInOverflow, placementIsComplete, placementLabel,
  OVERFLOW_LABEL, type ColdroomPlacement,
} from '@/lib/coldroomPlacement'
import ColdroomOverflowSection from '@/modules/packhouse/ColdroomOverflowSection'
import PlacePalletModal from '@/modules/packhouse/PlacePalletModal'
import DestinationRoomTabs from '@/components/DestinationRoomTabs'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlState, oneOf } from '@/hooks/useUrlState'
import { MapFocusPill } from '@/components/MapFocusPill'
import {
  AGE_BANDS, BAND_RANK, BAND_TILE_LABEL, bandFocusPendingText, bandFocusText, cellInBandFocus, cellTapAction,
  nextBandFocus, revealGridIfScrolledPast,
} from '@/lib/coldroomBandFocus'
import { coldroomReportHref } from '@/lib/coldroomReportLink'
import { formatGradeLabel } from '@/lib/gradeLabel'
import { withTimeout } from '@/lib/saveTimeout'
import BackdatePlacementModal from '@/components/BackdatePlacementModal'
import TableScroll from '@/components/TableScroll'
import ColdroomFind from '@/modules/packhouse/ColdroomFind'
import { PlaceFromProduction, PlaceAtCellModal } from '@/modules/packhouse/ColdroomPlaceFromProduction'
import SavedBanner, { SavedBannerLink } from '@/components/SavedBanner'
import ToLoadingBay, { offersLoadingBay } from '@/modules/packhouse/ColdroomToLoadingBay'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import type { ColdroomHit } from '@/lib/coldroomFind'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { useCanOpenAnyModule, useCanOpenModule } from '@/hooks/useCanOpenModule'
import { BIN_PAGE_MODULES } from '@/lib/modules'
import { usePermissions } from '@/lib/usePermissions'
import { isManagerOrAbove } from '@/lib/roles'
import { Money, useMoney } from '@/lib/money'
import { coldroomAccruingLine } from '@/lib/coldStorageWords'
import { splitRentalByStatus, type RevenueRow, type RoomPalletRow } from '@/lib/coldStorageSplit'
import { mustWithhold, combineReads, readState } from '@/lib/readState'
import SetupPointer from '@/components/SetupPointer'
import { useFlowManifest } from '@/hooks/useCommodityTemplates'
import { ACTIVE_COOLING_BATCHES_KEY, fetchActiveCoolingBatches } from '@/lib/activeCoolingBatches'
import { dolliesInRoom, dollyStageBreakdown } from '@/lib/coldroomReportRows'
import { BrandLoader } from '@/components/BrandLoader'
import ColdroomTemperatures, { ColdroomViewTabs, type ColdroomView } from '@/modules/packhouse/ColdroomTemperatures'
import { ColdroomRowCaption } from '@/modules/packhouse/ColdroomRowCaption'
import ColdTreatment from '@/modules/packhouse/ColdTreatment'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ColdroomPallet {
  pallet_id:       string
  status:          string
  pack_type:       string
  pallet_type:     string | null
  spec:            string | null
  carton_count:    number | null
  net_weight_kg:   number | null
  packed_at:       string
  stocked_at:      string | null
  version:         number
  // NULLABLE now. A pallet in the room's OVERFLOW has no cell by construction
  // (mig 20261021100001), and this being a plain `string` was what made the
  // grid read filter those rows out to keep the type honest.
  coldroom_grid:   string | null
  coldroom_overflow: boolean
  coldroom_id:     string | null
  run_id:          string
  grower:          string | null
  variety:         string | null
  // F05 UF-02: what the "To loading bay" lane picker and its export test need.
  tenant_id:       string | null
  channel:         string | null
  target_market:   string | null
  grade:           string | null
  size_code:       string | null
  destination:     string | null
  // U18 UF-02: the truck it is planned on (loading_missions embed); null when none or refused.
  mission_number:  string | null
}

interface Coldroom {
  coldroom_id:      string
  code:             string
  name:             string
  rows:             number
  cols:             number
  bin_stack_height: number
  sort_order:       number
  is_active:        boolean
}

interface ColdroomBin {
  bin_id:        string
  bin_number:    string
  status:        string
  version:       number
  net_weight_kg: number
  born_date:     string
  coldroom_id:   string | null
  coldroom_grid: string | null
  coldroom_overflow: boolean
  source_run_id: string
  grower:        string
  variety:       string
  size_code:     string | null
  grade:         string | null
}

// ---------------------------------------------------------------------------
// Grid parsing — coldroom cells are typed as letter+digit, e.g. A1, B12, AA3.
// ---------------------------------------------------------------------------
const GRID_RE = /^([A-Z]+)\s*(\d+)$/i

function parseCell(grid: string): { row: string; col: number } | null {
  const m = grid.trim().match(GRID_RE)
  if (!m) return null
  return { row: m[1].toUpperCase(), col: parseInt(m[2], 10) }
}

// Convert a row letter like "A" or "AB" to a sortable index.
function rowIndex(row: string): number {
  let n = 0
  for (const ch of row) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n
}

// ---------------------------------------------------------------------------
// Cell colour — empty = gray; occupied takes the WORST band of any pallet
// in it (one over-limit pallet should turn the cell red even if others are
// fresh).
// ---------------------------------------------------------------------------
const BAND_MARK_WORD: Record<AgeBand, string> = { ok: 'OK', warning: 'Warning', critical: 'Critical', over: 'Over limit' }

function cellColour(worst: AgeBand | null, count: number): string {
  if (count === 0) return 'bg-surface/40 hover:bg-surface border-line-subtle'
  switch (worst) {
    case 'over':     return 'bg-red-700 hover:bg-red-600 border-red-500 text-on-fill animate-pulse'
    case 'critical': return 'bg-red-900/70 hover:bg-red-800 border-red-700 text-red-100'
    case 'warning':  return 'bg-amber-900/70 hover:bg-amber-800 border-amber-700 text-amber-100'
    default:         return 'bg-emerald-900/70 hover:bg-emerald-800 border-emerald-700 text-emerald-100'
  }
}

// Age bands for the stock that is in the room but on NO cell — the overflow
// and the no-cell gaps. Module-level and pure so the page's `totals` memo stays
// one addition rather than four loops; separated from the cell-keyed tally
// because those two answer different questions (see the note on `totals`).
function offGridBands(
  pallets: ColdroomPallet[],
  bins:    ColdroomBin[],
  flow:    ReturnType<typeof useProductionFlow>,
): { bands: Record<AgeBand, number>; count: number } {
  const bands: Record<AgeBand, number> = { ok: 0, warning: 0, critical: 0, over: 0 }
  for (const p of pallets) {
    bands[getAgeStatus(effectiveAgeStart(p), isExportPallet(p), true, flow).band] += 1
  }
  for (const b of bins) {
    // Bins age from born_date — the original processing date, preserved
    // through rebins, same rule the cell buckets use.
    bands[getAgeStatus(b.born_date, false, true, flow).band] += 1
  }
  return { bands, count: pallets.length + bins.length }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
// Pre-existing large page; this change only adds an error/offline gate before
// the grid. Not newly complex — inherited size.
// fallow-ignore-next-line complexity
export default function ColdroomPage() {
  // Cold-chain age thresholds are per production flow — berry warns in hours,
  // citrus in days. Without this the whole coldroom aged berry pallets on the
  // citrus policy (green for ~3 weeks vs amber at 24h).
  const flow = useProductionFlow()
  const [selectedCell, setSelectedCell] = useState<string | null>(null)
  // Bins ticked for transfer (drives the transfer modal). Cleared when the
  // drawer closes or transfer succeeds.
  const [pickedBinIds, setPickedBinIds] = useState<Set<string>>(new Set())
  const [showTransfer, setShowTransfer] = useState(false)
  // Bins handed to the transfer modal from the OVERFLOW section rather than
  // from a cell drawer — same modal, no source cell.
  const [overflowBinIds, setOverflowBinIds] = useState<string[] | null>(null)
  // The pallet being put back onto the grid (or into the overflow) from the
  // off-grid list.
  const [placingPalletId, setPlacingPalletId] = useState<string | null>(null)
  // F05 UF-01: a pallet still in production found by scan, or an empty cell
  // tapped, opens the Place sheet from the map.
  const [awaitingHit, setAwaitingHit] = useState<Extract<ColdroomHit, { kind: 'awaiting' }> | null>(null)
  const [emptyCell, setEmptyCell] = useState<string | null>(null)
  // Pallet whose placement date is being back-dated (opens the modal).
  const [backdatePallet, setBackdatePallet] = useState<ColdroomPallet | null>(null)
  // F05 UF-02: the pallet whose lane is being picked ("To loading bay").
  const [laneForPallet, setLaneForPallet] = useState<ColdroomPallet | null>(null)
  // UF-01: the done panel after To loading bay. Stays until dismissed or the next send.
  const [sentToBay, setSentToBay] = useState<{ palletId: string; lane: string; cell: string | null } | null>(null)
  const activeTenantId = useActiveTenantId()
  // Back-dating a placement date rewrites the aging clock (not the room-day storage bill), so
  // gate it like the packed_at back-date on Pallet Detail: manager+ or a custom
  // role holding pallets:override.
  const { data: me } = useCurrentUser()
  const { can: canPerm } = usePermissions()
  const canBackdate = isManagerOrAbove(me?.role) || canPerm('pallets', 'override')
  // Moving a pallet to another cell is open to everyone who can open the
  // Coldroom (UF-12): the same write as "Place on grid", which is ungated, and
  // the move is recorded the same way (update_reason 'coldroom cell move').
  // A bin's page and its label open to Bin Inventory, Rebin, Coldroom or QC
  // (BIN_PAGE_MODULES, the route gate's own rule). Rows stay text while the
  // gate's reads decide.
  const canOpenBins = useCanOpenAnyModule(BIN_PAGE_MODULES) === true
  // The dolly cards open the Force-Cooling board, gated gPack('precool'),
  // which a LOADER lacks while RLS still lets him read the dollies: he gets
  // the card as text, no chevron, not a link that lands on Home (UF-05/06).
  const canOpenPrecool = useCanOpenModule('precool') === true
  const canOpenDispatch = useCanOpenModule('dispatch') === true
  // Active room persists across navigation — operators usually live in
  // one coldroom and bouncing back to "no room selected" every time they
  // looked at a pallet was annoying.
  const [activeRoomId, setActiveRoomId] = usePersistedState<string | null>('kf49.coldroom.activeRoomId', null)
  // The band tile tapped (F05 UF-03): the grid shows only the cells at that
  // band or worse. In the address, so Back from a pallet returns to the same
  // view; '' = the whole room.
  // Map | Temperatures (R-A7). In the address, so Back from the print page
  // returns to the tab; 'map' is the default and is left out of the address.
  const [view, setView] = useUrlState<ColdroomView>('view', 'map', v => (v === 'temps' || v === 'treatment' ? v : 'map'), { elide: true })
  const [bandFocus, setBandFocus] = useUrlState<'' | AgeBand>(
    'band', '', oneOf<AgeBand>(...AGE_BANDS), { elide: true })

  // A find, or a link from elsewhere, lands on the spot: the room switches and
  // the cell's drawer opens, or the page scrolls to the room's overflow
  // (PM-CST-04, PB-11). The drawer fills in as that room's pallets load.
  function openHit(hit: { kind: 'cell'; roomId: string; cell: string } | { kind: 'overflow'; roomId: string }) {
    setActiveRoomId(hit.roomId)
    if (hit.kind === 'cell') { setSelectedCell(hit.cell); return }
    setSelectedCell(null)
    window.setTimeout(() => document.getElementById('coldroom-overflow')?.scrollIntoView({ block: 'start' }), 300)
  }

  // ?room=<id>&cell=A3 (or &overflow=1) — a bin's "View map →" used to land on
  // whatever room was open last, with no cell (PM-INB-15). Read once, then
  // dropped from the URL so a refresh does not re-open the drawer.
  const [params, setParams] = useSearchParams()
  useEffect(() => {
    const room = params.get('room')
    if (!room) return
    const cell = params.get('cell')
    if (cell) openHit({ kind: 'cell', roomId: room, cell: cell.toUpperCase() })
    else if (params.get('overflow')) openHit({ kind: 'overflow', roomId: room })
    else setActiveRoomId(room)
    setParams(prev => paramsPatch(prev, { room: null, cell: null, overflow: null }), { replace: true })
  }, [params]) // eslint-disable-line react-hooks/exhaustive-deps

  // List of coldrooms for the tab bar. Showing inactive rooms too so old
  // pallets stuck in a deactivated room are still visible.
  //
  // ⚠️ Held as a query object, not destructured to `data: rooms = []`. This is
  // the read whose failure used to enter the page INVISIBLY: `rooms = []` →
  // effectiveRoomId null → palletsQ `enabled: false` → and a DISABLED query is
  // readState 'loading', never 'unread', so the mustWithhold(palletsQ) guard
  // below returned false and the header stated "0 pallets across 0 cells" over
  // a full empty grid. A guard on a query that can be disabled is off exactly
  // when it is needed; gate on the read that actually failed instead. The
  // general rule and its two remedies live on ReadState in src/lib/readState.ts
  // — this page is that note's worked example.
  const roomsQ = useQuery({
    queryKey: ['coldrooms-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coldrooms')
        .select('coldroom_id, code, name, rows, cols, bin_stack_height, sort_order, is_active')
        .order('sort_order').order('code')
      if (error) throw error
      return data as Coldroom[]
    },
  })
  const rooms = roomsQ.data ?? []

  // Default to the first active room once the list lands.
  const effectiveRoomId = useMemo(() => {
    if (activeRoomId && rooms.some(r => r.coldroom_id === activeRoomId)) return activeRoomId
    return rooms.find(r => r.is_active)?.coldroom_id ?? rooms[0]?.coldroom_id ?? null
  }, [activeRoomId, rooms])

  const activeRoom = rooms.find(r => r.coldroom_id === effectiveRoomId)

  const palletsQ = useQuery({
    queryKey: ['coldroom-grid', effectiveRoomId],
    enabled: !!effectiveRoomId,
    queryFn: async () => {
      // PAGED. This was a bare .select() with no bound: PostgREST caps a
      // response at 1,000 rows, so a room holding more than that drew a grid
      // MISSING pallets and looked complete — the operator's map of the room
      // is the one thing on this page that has to be whole. The dal gate did
      // not catch it because `carton_count:` in the select list satisfied its
      // `count:` test, which is fixed separately.
      const { rows: data, error } = await fetchAllPaged<Record<string, unknown>>((from, to) =>
        supabase
          .from('pallets')
          .select(`pallet_id, status, pack_type, pallet_type, spec, carton_count,
                   net_weight_kg, packed_at, stocked_at, version, coldroom_grid,
                   coldroom_overflow, coldroom_id, run_id, tenant_id, channel,
                   target_market, grade, size_code, destination,
                   runs:run_id(grower, variety),
                   mission:loading_missions!pallets_loading_mission_id_fkey(mission_number)`)
          .eq('status', 'IN_COLDROOM')
          .eq('coldroom_id', effectiveRoomId!)
          .is('deleted_at', null)
          // A paged read needs a TOTAL order, or a page can skip or repeat a
          // pallet and the map drawn from it is not the room (U18 D5).
          .order('pallet_id')
          .range(from, to))
      if (error) throw new Error(error)
      type Row = Omit<ColdroomPallet, 'grower' | 'variety' | 'mission_number'> & {
        runs: { grower: string; variety: string } | null
        mission: { mission_number: string | null } | { mission_number: string | null }[] | null
      }
      const rows = (data ?? []) as unknown as Row[]
      return rows.map(r => ({
        pallet_id:     r.pallet_id,
        status:        r.status,
        pack_type:     r.pack_type,
        pallet_type:   r.pallet_type,
        spec:          r.spec,
        carton_count:  r.carton_count,
        net_weight_kg: r.net_weight_kg,
        packed_at:     r.packed_at,
        stocked_at:    r.stocked_at,
        version:       r.version,
        coldroom_grid: r.coldroom_grid,
        coldroom_overflow: r.coldroom_overflow,
        coldroom_id:   r.coldroom_id,
        run_id:        r.run_id,
        grower:        r.runs?.grower ?? null,
        variety:       r.runs?.variety ?? null,
        tenant_id:     r.tenant_id ?? null,
        channel:       r.channel ?? null,
        target_market: r.target_market ?? null,
        grade:         r.grade ?? null,
        size_code:     r.size_code ?? null,
        destination:   r.destination ?? null,
        mission_number: (Array.isArray(r.mission) ? r.mission[0] : r.mission)?.mission_number ?? null,
      })) as ColdroomPallet[]
    },
    refetchInterval: POLL.standard,
  })
  const pallets = palletsQ.data ?? []
  const isLoading = palletsQ.isLoading

  // Bins in this room — same status/coldroom filter as pallets.
  const binsQ = useQuery({
    queryKey: ['coldroom-bins', effectiveRoomId],
    enabled: !!effectiveRoomId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bins')
        // coldroom_id is SELECTED, not only filtered on: locationKind() reads
        // it, and without it every bin read as 'noroom' — off the grid, on the
        // "no cell recorded" list — however it was placed (found 7 Oct).
        .select('bin_id, bin_number, status, version, net_weight_kg, born_date, coldroom_id, coldroom_grid, coldroom_overflow, source_run_id, grower, variety, size_code, grade')
        .eq('status', 'IN_COLDROOM')
        .eq('coldroom_id', effectiveRoomId!)
        .is('deleted_at', null)
      if (error) throw error
      return (data ?? []) as ColdroomBin[]
    },
    refetchInterval: POLL.standard,
  })
  const bins = binsQ.data ?? []

  // Counts per room for the tab badges — all in one round trip.
  const roomCountsQ = useQuery({
    queryKey: ['coldroom-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pallets')
        .select('coldroom_id, coldroom_grid, coldroom_overflow')
        .eq('status', 'IN_COLDROOM')
        .not('coldroom_id', 'is', null)
        // L56: a soft-deleted pallet KEEPS status='IN_COLDROOM' and its
        // coldroom_id — which is exactly why the grid read above carries this
        // filter. Without it the room's tab badge counted pallets that no
        // longer exist and disagreed with the grid and the header totals on
        // the same screen: "Room A (5)" over a grid showing four.
        .is('deleted_at', null)
        .range(0, 49_999)
      if (error) throw error
      // `total` is every pallet in the room, as the badge always counted.
      // `overflow` is the subset deliberately off the grid, so a room that is
      // over its map shows it on the TAB — before anyone opens it. Pallets
      // only, like the badge it sits beside; the page's overflow card is where
      // bins are counted, and mixing units on one badge would make "+3" mean
      // nothing in particular.
      const out: Record<string, { total: number; overflow: number }> = {}
      for (const p of (data ?? []) as { coldroom_id: string; coldroom_grid: string | null; coldroom_overflow: boolean }[]) {
        const e = (out[p.coldroom_id] ??= { total: 0, overflow: 0 })
        e.total += 1
        if (isInOverflow(p)) e.overflow += 1
      }
      return out
    },
    refetchInterval: POLL.standard,
  })
  const roomCounts = roomCountsQ.data ?? {}
  // A failed badge read shows a dash, never a missing badge that reads as an
  // empty room (U18 D5).
  const countsUnread = readState(roomCountsQ) === 'unread'

  // Untipped delivery bins recorded as standing in each room (room-level, no
  // grid cell — spec: delivery-coldroom-placement). One fetch across rooms.
  const { data: placedByRoom = {} } = useQuery({
    queryKey: ['coldroom-placed-deliveries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deliveries')
        .select('coldroom_id, bins_remaining')
        .eq('status', 'IN_STOCK')
        .not('coldroom_id', 'is', null)
        .gt('bins_remaining', 0)
        .is('deleted_at', null)
        .range(0, 49_999)
      if (error) throw error
      const out: Record<string, { bins: number; deliveries: number }> = {}
      for (const d of (data ?? []) as { coldroom_id: string; bins_remaining: number }[]) {
        const e = (out[d.coldroom_id] ??= { bins: 0, deliveries: 0 })
        e.bins += d.bins_remaining
        e.deliveries += 1
      }
      return out
    },
    refetchInterval: POLL.standard,
  })
  const placedHere = effectiveRoomId ? placedByRoom[effectiveRoomId] : undefined

  // Dollies standing in this room (F05 UF-06). A berry force-cool or holding
  // room is full of cooling batches, not pallets, and the map never read them:
  // it said "0 pallets" over a full room. The register's own read, under its
  // own key (one cache), gated on the flow the way the register gates it, so
  // a citrus business never fetches dollies. Room-level only: no screen
  // records a dolly's position, so they stay off the cell grid.
  const manifest = useFlowManifest(flow)
  const coolingQ = useQuery({
    queryKey: ACTIVE_COOLING_BATCHES_KEY,
    enabled: manifest.usesForceCooling,
    queryFn: fetchActiveCoolingBatches,
    refetchInterval: POLL.standard,
  })
  const dolliesHere = useMemo(() => (
    effectiveRoomId && coolingQ.data
      ? dolliesInRoom(coolingQ.data.batches, coolingQ.data.stageRoomIds, effectiveRoomId)
      : null
  ), [effectiveRoomId, coolingQ.data])
  const dolliesUnread = manifest.usesForceCooling && readState(coolingQ) === 'unread'

  // Bucket pallets + bins by cell + compute worst age band per cell.
  // Rows/cols come from the active room's configured dimensions so the
  // grid renders the full layout (not just cells that happen to be occupied).
  //
  // FOUR DESTINATIONS, not two. Since mig 20261021100001 a row reaching this
  // page may have no cell at all, and there are two quite different reasons for
  // that — see src/lib/coldroomPlacement.ts. `locationKind` is the one place
  // that decides which, so the grid, the overflow section and the register
  // cannot drift apart about the same pallet:
  //
  //   cell      → bucketed onto the map below (or into unparseable/outOfBounds)
  //   overflow  → the operator put it in the room, off the map, on purpose
  //   nocell    → in the room with nothing recorded about where. A gap, and it
  //               gets its own amber list rather than being quietly counted as
  //               an overflow placement nobody made.
  //
  // These rows were invisible on this page until now: the two reads filtered
  // `coldroom_grid IS NOT NULL`, so a cell-less pallet was counted in the room
  // TAB BADGE (which never had that filter) and in nothing else. The badge
  // disagreeing with the header was the only symptom.
  const {
    palletsByCell, binsByCell, rows, cols, summary, unparseable, outOfBounds,
    overflowPallets, overflowBins, strandedPallets, strandedBins,
  } = useMemo(() => {
    const palletBuckets = new Map<string, ColdroomPallet[]>()
    const binBuckets    = new Map<string, ColdroomBin[]>()
    const unparseableList: ColdroomPallet[] = []
    const outOfBoundsList: ColdroomPallet[] = []
    const overflowP: ColdroomPallet[] = []
    const overflowB: ColdroomBin[]    = []
    const strandedP: ColdroomPallet[] = []
    const strandedB: ColdroomBin[]    = []

    const maxRows = activeRoom?.rows ?? 8
    const maxCols = activeRoom?.cols ?? 12

    for (const p of pallets) {
      const where = locationKind(p)
      if (where === 'overflow') { overflowP.push(p); continue }
      if (where !== 'cell')     { strandedP.push(p); continue }
      const parsed = parseCell(p.coldroom_grid!)
      if (!parsed) { unparseableList.push(p); continue }
      if (rowIndex(parsed.row) > maxRows || parsed.col < 1 || parsed.col > maxCols) {
        outOfBoundsList.push(p); continue
      }
      const key = `${parsed.row}${parsed.col}`
      if (!palletBuckets.has(key)) palletBuckets.set(key, [])
      palletBuckets.get(key)!.push(p)
    }

    for (const b of bins) {
      const where = locationKind(b)
      if (where === 'overflow') { overflowB.push(b); continue }
      if (where !== 'cell')     { strandedB.push(b); continue }
      const parsed = parseCell(b.coldroom_grid!)
      if (!parsed) continue   // surfaced via the bins list separately if needed
      if (rowIndex(parsed.row) > maxRows || parsed.col < 1 || parsed.col > maxCols) continue
      const key = `${parsed.row}${parsed.col}`
      if (!binBuckets.has(key)) binBuckets.set(key, [])
      binBuckets.get(key)!.push(b)
    }

    const rowsArr = Array.from({ length: maxRows }, (_, i) => String.fromCharCode(65 + i))
    const colsArr = Array.from({ length: maxCols }, (_, i) => i + 1)

    // `worst` drives the CELL COLOUR (one colour per cell — that part is
    // right). `bands` counts each stored item under its OWN band, because the
    // header tiles are counts of ITEMS: attributing all 4 bins in a cell to the
    // cell's worst band inflated the over-limit alarm by up to (stack height −
    // 1) per affected cell, and zeroed the 'OK' count for bins that are fine.
    type Cell = {
      worst: AgeBand | null; palletCount: number; binCount: number
      bands: Record<AgeBand, number>
    }
    const emptyBands = (): Record<AgeBand, number> => ({ ok: 0, warning: 0, critical: 0, over: 0 })
    const summaryMap = new Map<string, Cell>()
    const ensure = (k: string) => {
      if (!summaryMap.has(k)) summaryMap.set(k, { worst: null, palletCount: 0, binCount: 0, bands: emptyBands() })
      return summaryMap.get(k)!
    }
    const bumpWorst = (cell: Cell, band: AgeBand) => {
      cell.bands[band] += 1
      if (cell.worst == null || BAND_RANK[band] > BAND_RANK[cell.worst]) cell.worst = band
    }

    for (const [key, ps] of palletBuckets) {
      const cell = ensure(key)
      cell.palletCount = ps.length
      for (const p of ps) bumpWorst(cell, getAgeStatus(effectiveAgeStart(p), isExportPallet(p), true, flow).band)
    }
    for (const [key, bs] of binBuckets) {
      const cell = ensure(key)
      cell.binCount = bs.length
      // Bins age from born_date — the original processing date, preserved
      // through rebins so we don't get a freshness illusion.
      for (const b of bs) {
        bumpWorst(cell, getAgeStatus(b.born_date, false, true, flow).band)
      }
    }

    return {
      palletsByCell:   palletBuckets,
      binsByCell:      binBuckets,
      rows:            rowsArr,
      cols:            colsArr,
      summary:         summaryMap,
      unparseable:     unparseableList,
      outOfBounds:     outOfBoundsList,
      overflowPallets: overflowP,
      overflowBins:    overflowB,
      strandedPallets: strandedP,
      strandedBins:    strandedB,
    }
  }, [pallets, bins, activeRoom, flow])

  // Aggregate counts for the header strip. The age bands (ok/warning/critical/
  // over) count every stored item; pallets and bins are tracked SEPARATELY so
  // the header doesn't label bins as pallets (L30).
  //
  // ⚠️ THE BANDS COUNT THE OFF-GRID STOCK TOO (mig 20261021100001). Fruit in
  // the room's overflow is in exactly the same cold air as fruit on the map and
  // ages at exactly the same rate. Summing only `summary` — the cell-keyed
  // buckets — would make the over-limit alarm quietly SMALLER the fuller the
  // room got, which is the reverse of what this strip is for: the overflow
  // fills up precisely when the packhouse is under pressure. The pallet / bin /
  // cell counts stay grid-only and are labelled as such in the header; the age
  // bands are a claim about the fruit, not about the map, and do not.
  const totals = useMemo(() => {
    let ok = 0, warning = 0, critical = 0, over = 0
    let pallets = 0, bins = 0
    for (const v of summary.values()) {
      pallets += v.palletCount
      bins    += v.binCount
      if (!v.worst) continue
      // Per-item bands, not the cell's worst applied to every item in it.
      ok       += v.bands.ok
      warning  += v.bands.warning
      critical += v.bands.critical
      over     += v.bands.over
    }
    const offGrid = offGridBands(
      [...overflowPallets, ...strandedPallets],
      [...overflowBins, ...strandedBins],
      flow,
    )
    return {
      ok:       ok       + offGrid.bands.ok,
      warning:  warning  + offGrid.bands.warning,
      critical: critical + offGrid.bands.critical,
      over:     over     + offGrid.bands.over,
      pallets, bins, cells: summary.size,
      offGrid: offGrid.count,
    }
  }, [summary, overflowPallets, overflowBins, strandedPallets, strandedBins, flow])

  // The header line and the four band tiles are the same claim in two places:
  // what is physically standing in this room right now. It takes THREE reads to
  // be true — the room list (which room, and how big it is), the pallets in it,
  // and the bins in it — and two of those three can be `enabled: false`.
  // combineReads is exactly this: the claim is only as good as its weakest
  // input. `stat` is what stops a number we do not hold rendering as a zero.
  const gridRead = combineReads(roomsQ, palletsQ, binsQ)
  // Row security refuses another business's pallet, so the button is theirs only.
  const ownLoadingBay = (p: { channel: string | null; target_market: string | null; tenant_id: string | null }) =>
    !!activeTenantId && p.tenant_id === activeTenantId && offersLoadingBay(p)
  const stat = (n: number) => (gridRead.state === 'usable' ? String(n) : '—')

  // Cells left on the map under the band focus, for the focus line.
  const focusedCells = useMemo(() => {
    if (!bandFocus) return 0
    let n = 0
    for (const v of summary.values()) if (cellInBandFocus(v.worst, bandFocus)) n += 1
    return n
  }, [summary, bandFocus])
  // Report and the delivery-bins card open the register on THIS room, and on
  // the lit band tile (F05 UF-04) — not bare, which made the manager pick the
  // room again.
  const reportHref = coldroomReportHref(effectiveRoomId, bandFocus)

  // A tile opens what it counts; tapping the lit tile again shows the room.
  // On a phone the tiles sit under the grid, so bring the grid back up.
  function toggleBand(band: AgeBand) {
    const next = nextBandFocus(bandFocus, band)
    setBandFocus(next)
    if (next) revealGridIfScrolledPast(document.getElementById('coldroom-grid'))
  }

  // Capacity rollup — how much physical space remains in the active room.
  // Pallet capacity = empty cells (a pallet needs an unoccupied cell).
  // Bin capacity    = empty cells × stack_height + remaining slots in cells
  //                   that already hold bins (up to stack_height).
  //
  // The overflow rides ALONGSIDE those figures and is never netted off them —
  // see the note at the top of coldroomCapacity.ts. "3 cells free" has to keep
  // meaning three cells, because a cell is what the picker hands out.
  const capacity = useMemo(
    () => (activeRoom
      ? computeColdroomCapacity(activeRoom, [...summary.values()], {
          pallets: overflowPallets.length,
          bins:    overflowBins.length,
        })
      : null),
    [summary, activeRoom, overflowPallets, overflowBins],
  )

  // Resolved here, not inside the modal: if the row has gone (someone else
  // moved it while this list was on screen) there is nothing to place and the
  // sheet simply does not open.
  // An off-grid pallet is PLACED (and keeps that audit reason — a stranded
  // pallet's bad grid value is not a cell it is moving off). An on-grid one,
  // from the cell drawer, is MOVED (PM-CST-05).
  const offGridPlacing = placingPalletId
    ? [...overflowPallets, ...strandedPallets].find(p => p.pallet_id === placingPalletId)
    : undefined
  const placingPallet = offGridPlacing ? { ...offGridPlacing, coldroom_grid: null }
    : placingPalletId ? pallets.find(p => p.pallet_id === placingPalletId) ?? null
    : null

  const selectedPallets = selectedCell ? palletsByCell.get(selectedCell) ?? [] : []
  const selectedBins    = selectedCell ? binsByCell.get(selectedCell)    ?? [] : []
  const sentBanner = sentToBay && (
    <SavedBanner title="Sent to the loading bay" reference={sentToBay.palletId}
      onDismiss={() => setSentToBay(null)}
      actions={<SavedBannerLink to={`/loading-bay?lane=${encodeURIComponent(sentToBay.lane)}`}>Open Floor Map</SavedBannerLink>}>
      <p>Lane {sentToBay.lane}.{sentToBay.cell ? ` It left cell ${sentToBay.cell}.` : ''}</p>
    </SavedBanner>
  )

  // Cell drawer — shows pallets in the selected cell. A <DetailShell>: a
  // pane beside the room from 1024 (PM-CST-11), a <Modal> below it.
  // The Modal's root is z-50, the same rung `z-modal` names, so it still
  // paints over the later-in-DOM BottomNav (mobile audit 2026-07-19, S4).
  // No onSubmit: the drawer's rows ARE its actions, so it keeps a non-form
  // root and no footer — Enter must not fire the batch move.
  // It does take a snapshot: the picks are a walk of the cell face, and a
  // stray backdrop tap used to bin them silently.
  const cellDetail = selectedCell && (
        <DetailShell
          title={
            <span className="flex items-center gap-2">
              <Snowflake size={16} className="text-emerald-400 shrink-0" />
              <span className="truncate">
                Cell <span className="font-mono text-emerald-400">{selectedCell}</span>
              </span>
              <span className="text-caption font-normal text-fg-muted shrink-0 tabular-nums">
                {selectedPallets.length > 0 && `${selectedPallets.length} pallet${selectedPallets.length === 1 ? '' : 's'}`}
                {selectedPallets.length > 0 && selectedBins.length > 0 && ' · '}
                {selectedBins.length > 0 && `${selectedBins.length} bin${selectedBins.length === 1 ? '' : 's'}`}
              </span>
            </span>
          }
          onClose={() => { setSelectedCell(null); setPickedBinIds(new Set()); setShowTransfer(false) }}
          snapshot={[...pickedBinIds].sort().join(',')}
          noFooter>
            <div className="-m-1 space-y-1.5">
              {sentBanner}
              {/* Bin transfer toolbar — appears whenever the cell holds bins */}
              {selectedBins.length > 0 && (
                <div className="bg-surface/40 border border-line rounded-lg px-2.5 py-1.5 flex items-center gap-2 text-caption">
                  {pickedBinIds.size === 0 ? (
                    <button
                      onClick={() => setPickedBinIds(new Set(selectedBins.map(b => b.bin_id)))}
                      className="text-fg-muted hover:text-fg min-h-[44px] px-2 inline-flex items-center">
                      Select all {selectedBins.length} for move
                    </button>
                  ) : (
                    <>
                      <span className="text-fg-secondary tabular-nums">{pickedBinIds.size} selected</span>
                      <button onClick={() => setPickedBinIds(new Set())}
                        className="text-fg-muted hover:text-fg min-h-[44px] px-2 inline-flex items-center">Clear</button>
                      <button
                        onClick={() => setShowTransfer(true)}
                        className="ml-auto rounded-lg px-3 min-h-[44px] text-caption font-semibold bg-cyan-700 hover:bg-cyan-600 text-on-fill">
                        Move →
                      </button>
                    </>
                  )}
                </div>
              )}

              {selectedBins
                .slice()
                .sort((a, b) => a.born_date.localeCompare(b.born_date))
                .map(b => {
                  const status = getAgeStatus(b.born_date, false, true, flow)
                  const isPicked = pickedBinIds.has(b.bin_id)
                  return (
                    <div key={b.bin_id} className="flex items-stretch gap-1.5">
                      {/* 44px hit area (gloved hands, FCX-09) — the visual
                          checkbox stays 20px inside a full-height tap zone. */}
                      <button
                        onClick={() => setPickedBinIds(s => {
                          const next = new Set(s)
                          next.has(b.bin_id) ? next.delete(b.bin_id) : next.add(b.bin_id)
                          return next
                        })}
                        className="shrink-0 w-11 min-h-[44px] self-stretch flex items-center justify-center"
                        aria-label="Pick for move">
                        <span className={clsx(
                          'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                          isPicked ? 'border-cyan-500 bg-cyan-500' : 'border-line hover:border-gray-400'
                        )}>
                          {isPicked && <span className="text-on-fill text-caption">✓</span>}
                        </span>
                      </button>
                      {/* The row opens the bin; Print label is a SIBLING, not a
                          button nested in the link — a gloved tap a few pixels
                          off it opened Bin Detail instead (PM-CST-12, CLD-11).
                          Without a role that opens bins both are gone and the row is text. */}
                      <div className="flex-1 min-w-0 flex items-stretch gap-1 bg-surface rounded-lg">
                      <BinRowShell to={canOpenBins ? `/bins/${b.bin_id}` : null} returnTo="/coldroom">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-mono text-caption text-cyan-300 truncate">
                              <span className="text-caption uppercase bg-cyan-900/50 text-cyan-200 rounded px-1 py-0.5 mr-1.5">BIN</span>
                              {b.bin_number}
                            </p>
                            <p className="text-caption text-fg-secondary truncate flex items-center gap-1.5">
                              <Package size={10} />
                              {Math.round(b.net_weight_kg)} kg net
                              {(b.size_code || b.grade) && (
                                <span className="text-emerald-300">
                                  · {b.size_code ?? ''}{b.size_code && b.grade ? ' · ' : ''}{b.grade ? formatGradeLabel(b.grade) : ''}
                                </span>
                              )}
                            </p>
                            <p className="text-caption text-fg-secondary truncate">
                              {b.grower} · {b.variety}
                            </p>
                          </div>
                          <span className={clsx('text-caption rounded-full px-2 py-0.5 font-semibold shrink-0', status.badgeClass)}>
                            {status.label}
                          </span>
                        </div>
                      </BinRowShell>
                      {canOpenBins && <button type="button"
                        onClick={() => window.open(`/bin-label/${b.bin_id}`, '_blank')}
                        aria-label={`Print the label for ${b.bin_number}`}
                        className="shrink-0 text-caption text-cyan-400 hover:text-cyan-300 min-h-[44px] px-2.5 flex items-center rounded-lg hover:bg-cyan-900/20">
                        Print label →
                      </button>}
                      </div>
                    </div>
                  )
                })}
              {selectedPallets
                .slice()
                .sort((a, b) => effectiveAgeStart(a).localeCompare(effectiveAgeStart(b)))
                .map(p => {
                  const status = getAgeStatus(effectiveAgeStart(p), isExportPallet(p), true, flow)
                  // The row opens the pallet; Move / Adjust date are SIBLINGS of
                  // that link, never buttons nested inside it (PM-CST-12).
                  return (
                    <div key={p.pallet_id} className="flex items-stretch gap-1 bg-surface rounded-lg">
                    <div className="flex-1 min-w-0 hover:bg-raised rounded-lg">
                    <Link
                      to={`/pallets/${p.pallet_id}`}
                      className="block p-2.5 pb-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-caption min-w-0"><PalletIdText id={p.pallet_id} className="text-fg-secondary" /></p>
                          <p className="text-caption text-fg-secondary truncate flex items-center gap-1.5">
                            <Package size={10} />
                            {p.pack_type} · {p.carton_count ?? '—'} ctns
                            {p.net_weight_kg && ` · ${Math.round(p.net_weight_kg)} kg`}
                          </p>
                          <p className="text-caption text-fg-secondary truncate">
                            {p.grower}{p.variety && ` · ${p.variety}`}
                          </p>
                        </div>
                        <span className={clsx('text-caption rounded-full px-2 py-0.5 font-semibold shrink-0', status.badgeClass)}>
                          {status.label}
                        </span>
                      </div>
                    </Link>
                    <div className="px-2.5 pb-2.5">
                      <ColdroomRowCaption channel={p.channel} targetMarket={p.target_market}
                        missionNumber={p.mission_number} canOpenDispatch={canOpenDispatch} />
                    </div>
                    </div>
                    <div className="shrink-0 flex flex-col justify-center">
                      {ownLoadingBay(p) && (
                        <button type="button" onClick={() => setLaneForPallet(p)}
                          aria-label={`Send ${p.pallet_id} to the loading bay`}
                          className="text-caption font-semibold text-on-fill bg-cyan-700 hover:bg-cyan-600 min-h-[44px] px-2.5 flex items-center rounded-lg">
                          To loading bay
                        </button>
                      )}
                      {/* Move a pallet to another cell right here — it used to
                          mean the Loading Bay map (PM-CST-05, PB-11). */}
                      <button type="button" onClick={() => setPlacingPalletId(p.pallet_id)}
                        aria-label={`Move ${p.pallet_id} to another cell`}
                        className="text-caption text-cyan-400 hover:text-cyan-300 min-h-[44px] px-2.5 flex items-center rounded-lg hover:bg-cyan-900/20">
                        Move →
                      </button>
                      {canBackdate && (
                        <button type="button" onClick={() => setBackdatePallet(p)}
                          aria-label={`Adjust the placement date of ${p.pallet_id}`}
                          className="text-caption text-cyan-400 hover:text-cyan-300 min-h-[44px] px-2.5 flex items-center rounded-lg hover:bg-cyan-900/20">
                          Adjust date →
                        </button>
                      )}
                    </div>
                    </div>
                  )
                })}
            </div>
        </DetailShell>
  )

  const viewTabs = <ColdroomViewTabs view={view} onChange={setView} />
  if (view === 'temps') return <ColdroomTemperatures tabs={viewTabs} />
  if (view === 'treatment') return <ColdTreatment tabs={viewTabs} />

  return (
    <div className="min-h-full bg-canvas flex flex-col">
      {/* PW-13: the room's name is the subtitle, not a second span in the h1 —
          there it wrapped word by word into a 63px column beside Report and
          made the pinned header 181px (packhouse audit PM-CST-01). */}
      <PageHeader title="Coldroom" icon={<Snowflake size={18} />}
        subtitle={activeRoom?.name}
        actions={
          <Link
            to={reportHref}
            className="shrink-0 flex items-center gap-1.5 min-h-[44px] rounded-lg bg-surface hover:bg-raised text-fg text-caption font-semibold px-3">
            <FileText size={14} /> Report
          </Link>
        } />

      {viewTabs}

      {/* The room's counts, under the header rather than in it: as its second
          context line they would truncate on a phone, and the amber
          "+N off the grid" is the one part nobody may lose. */}
      <p className="px-4 py-2 border-b hairline text-caption text-fg-muted tabular-nums">
        {stat(totals.pallets)} pallets{totals.bins > 0 ? ` · ${totals.bins} bins` : ''} across {stat(totals.cells)} cells
        {activeRoom && <> · {activeRoom.rows} × {activeRoom.cols} layout</>}
        {/* Stated separately, never added in: the counts above are "on the
            map", and rolling the aisle into them would make the map look
            like it holds more cells than it has. Behind `stat` for the same
            reason every other figure on this line is. */}
        {gridRead.state === 'usable' && totals.offGrid > 0 && (
          <span className="text-warning"> · +{totals.offGrid} off the grid</span>
        )}
      </p>

      {/* Room selector — tabs across the top */}
      {rooms.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto px-4 py-2.5 border-b border-line-subtle bg-canvas/40">
          {rooms.map(r => (
            <button
              key={r.coldroom_id}
              onClick={() => setActiveRoomId(r.coldroom_id)}
              className={clsx(
                'shrink-0 rounded-full px-3 py-1.5 text-caption font-semibold transition-colors flex items-center gap-1.5',
                effectiveRoomId === r.coldroom_id
                  ? 'bg-cyan-600 text-on-fill'
                  : 'bg-surface text-fg-muted hover:bg-raised hover:text-fg-secondary',
                !r.is_active && 'opacity-60',
              )}
              title={r.is_active ? r.name : `${r.name} (inactive)`}>
              <span>{r.code}</span>
              {countsUnread && (
                <span className="opacity-70 text-caption tabular-nums" title="Could not read the count">· –</span>
              )}
              {!countsUnread && (roomCounts[r.coldroom_id]?.total ?? 0) > 0 && (
                <span className="opacity-70 text-caption tabular-nums">· {roomCounts[r.coldroom_id]!.total}</span>
              )}
              {/* "+N" in amber — the room holds more than its grid. Stated as
                  an addition rather than folded into the count, so the total
                  keeps meaning what it always meant. */}
              {!countsUnread && (roomCounts[r.coldroom_id]?.overflow ?? 0) > 0 && (
                <span
                  className={clsx('text-caption font-bold tabular-nums', effectiveRoomId === r.coldroom_id ? 'text-amber-200' : 'text-warning')}
                  title={`${roomCounts[r.coldroom_id]!.overflow} in the ${OVERFLOW_LABEL.toLowerCase()}`}>
                  +{roomCounts[r.coldroom_id]!.overflow}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* From 1024 an open cell sits BESIDE the room, not over it (packhouse
          audit PM-CST-11): on a door-mounted landscape tablet the drawer was a
          centred modal that hid the very grid the operator is working. Below
          1024 it is still a <Modal> — <DetailShell> picks, from one position,
          so a tablet rotated with bins picked keeps the picks. The padding
          lives on this wrapper so the pane's top lines up with the room's. */}
      <div className="flex-1 p-4 pb-12">
      <SplitPane detail={selectedCell ? cellDetail : null}>
      {/* A flex column so the grid can lead on a phone (see the grid below);
          overflow-x-clip, not a dead overflow-y-auto — the root is min-h-full
          and the window is what scrolls. */}
      <div className="overflow-x-clip flex flex-col gap-4">
        {/* Everything below describes one room, and all of it hangs off the room
            list: no room means no layout, no pallet query and no bin query. So
            this is where a failed rooms read has to stop — the alternative is
            the 8 × 12 fallback grid under four invented zeros. */}
        {mustWithhold(roomsQ) ? (
          <QueryStateGate query={roomsQ} noun="coldrooms" empty={<div />} />
        ) : roomsQ.data && !effectiveRoomId ? (
          <section className="card p-6 text-center">
            <Snowflake size={20} className="mx-auto mb-2 text-muted" />
            <p className="text-sm text-fg-secondary">No coldrooms are configured yet.</p>
            <p className="text-caption text-muted mt-1">
              <SetupPointer target="coldrooms">{link => <>Add one under {link} and its layout grid appears here.</>}</SetupPointer>
            </p>
          </section>
        ) : (
        <>
        {/* Find a pallet or bin — typed or scanned (PM-CST-04, PB-11). Above
            the grid on every width: it is how you get to the right cell. */}
        {!selectedCell && sentBanner && <div className="max-sm:order-first">{sentBanner}</div>}
        <div className="max-sm:order-first">
          <ColdroomFind roomCode={id => rooms.find(r => r.coldroom_id === id)?.code ?? 'another room'}
            onHit={openHit} onAwaiting={setAwaitingHit} />
        </div>

        {/* Summary strip */}
        <section className="grid grid-cols-4 gap-2">
          {AGE_BANDS.map(b => (
            <Stat key={b} label={BAND_TILE_LABEL[b]} value={stat(totals[b])} tone={STAT_TONE[b]}
              selected={bandFocus === b} onClick={() => toggleBand(b)} />
          ))}
        </section>

        {/* On a phone the rental and space cards fold under the grid: with
            them and the tiles above it, the map started at y≈740 of 844 —
            every visit began with a scroll (PM-CST-03, PB-11). From sm they
            sit where they always did. */}
        <PhoneFold label="Room summary · rental and space">
        {/* Cold-storage running cost across pallets currently in this room */}
        <ColdStorageAccrued coldroomId={effectiveRoomId} />

        {/* Bulk delivery bins standing in this room — room-level placement,
            so they occupy floor space the cell grid below can't show. */}
        {placedHere && (
          <Link
            to={reportHref}
            className="card p-3 flex items-center gap-2.5 border border-cyan-800/60 bg-cyan-900/20 hover:bg-cyan-900/30">
            <Snowflake size={15} className="text-cyan-400 shrink-0" />
            <p className="text-sm text-cyan-100 flex-1">
              + {placedHere.bins} delivery bin{placedHere.bins === 1 ? '' : 's'} ({placedHere.deliveries} deliver{placedHere.deliveries === 1 ? 'y' : 'ies'}, untipped)
              <span className="text-cyan-300/70"> — not on the cell grid; listed on the register</span>
            </p>
          </Link>
        )}

        {/* Dollies on the force-cooling board standing in this room (F05
            UF-06) — same card as the delivery bins above; opens the board for
            a viewer who can open it, plain text otherwise. A failed read says
            so and states no count. */}
        {manifest.usesForceCooling && dolliesHere && dolliesHere.total > 0 && (
          <PrecoolCard canOpen={canOpenPrecool}
            className="border-cyan-800/60 bg-cyan-900/20" hoverClass="hover:bg-cyan-900/30"
            chevronClass="text-cyan-300/70">
            <Snowflake size={15} className="text-cyan-400 shrink-0" />
            <p className="text-sm text-cyan-100 flex-1">
              + {dolliesHere.total} {dolliesHere.total === 1 ? 'dolly' : 'dollies'} on the force-cooling board ({dollyStageBreakdown(dolliesHere)})
              <span className="text-cyan-300/70"> — not on the cell grid</span>
            </p>
          </PrecoolCard>
        )}
        {dolliesUnread && (
          <PrecoolCard canOpen={canOpenPrecool}
            className="border-warning-edge" hoverClass="hover:bg-surface/60"
            chevronClass="text-amber-300/70">
            <AlertTriangle size={15} className="text-warning shrink-0" />
            <p className="text-sm text-warning flex-1">
              {canOpenPrecool
                ? "Couldn't count the dollies in this room — open the force-cooling board"
                : "Couldn't count the dollies in this room"}
            </p>
          </PrecoolCard>
        )}

        {/* Capacity strip — what physically fits in here right now.
            ⚠️ Gated on the SAME read as the grid below. Every figure here folds
            from `pallets = palletsQ.data ?? []`, so a failed pallets read made
            the strip state "0 cells hold a pallet · N free" over a coldroom
            that may be full — and the grid's own gate (further down) could not
            protect it, because this sits ABOVE that branch. Found by the
            partial-failure sweep (§25), second find; the first was the builder
            chips, also a sibling the grid's guard did not reach. */}
        {capacity && !mustWithhold(palletsQ) && (
          <section className="card p-3 sm:p-4">
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-caption font-semibold uppercase tracking-wider text-fg-muted">
                Available space
              </p>
              <p className="text-caption text-fg-muted tabular-nums">
                {capacity.totalCells} cells total
                {capacity.stackHeight > 1 && <> · stack {capacity.stackHeight}</>}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CapacityRow
                label="Pallet space"
                remaining={capacity.palletRemaining}
                total={capacity.totalCells}
                used={capacity.totalCells - capacity.emptyCells}
                pctUsed={capacity.palletPctUsed}
                colour="bg-indigo-500"
                hint={`${capacity.palletCells} cell${capacity.palletCells === 1 ? '' : 's'} hold a pallet · ${capacity.binCells} hold bins`}
              />
              <CapacityRow
                label="Bin space"
                remaining={capacity.binRemaining}
                total={capacity.totalCells * capacity.stackHeight}
                used={capacity.binsStored}
                pctUsed={capacity.binPctUsed}
                colour="bg-cyan-500"
                hint={capacity.stackHeight > 1
                  ? `${capacity.binsStored} bin${capacity.binsStored === 1 ? '' : 's'} stored · room for ${capacity.binRemaining} more`
                  : `${capacity.binsStored} bin${capacity.binsStored === 1 ? '' : 's'} stored`}
              />
            </div>

            {/* Beside the two bars, never inside them. The bars answer "how
                much more fits ON THE GRID", which is the only question the
                picker can act on; this answers "is the room already holding
                more than the grid can describe", which is the one the floor
                needs. Netting one off the other would destroy both answers. */}
            {capacity.overGrid && (
              <p className="mt-3 pt-2.5 border-t border-line-subtle text-caption text-warning flex items-center gap-1.5">
                <Layers size={12} className="shrink-0" />
                <span>
                  <span className="font-semibold">Over the grid</span> —{' '}
                  {capacity.overflowPallets > 0 && `${capacity.overflowPallets} pallet${capacity.overflowPallets === 1 ? '' : 's'}`}
                  {capacity.overflowPallets > 0 && capacity.overflowBins > 0 && ' · '}
                  {capacity.overflowBins > 0 && `${capacity.overflowBins} bin${capacity.overflowBins === 1 ? '' : 's'}`}
                  {' '}in the {OVERFLOW_LABEL.toLowerCase()}, on top of the {capacity.totalCells} cells above.
                </span>
              </p>
            )}
          </section>
        )}
        </PhoneFold>

        {/* Heatmap grid — always rendered (even when the room is empty) so
            the operator sees the layout. Empty cells render as ghost
            buttons that don't open the cell drawer. First on a phone: the
            map is what the operator came for (PM-CST-03). */}
        <div id="coldroom-grid" className="max-sm:order-first scroll-mt-24">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <BrandLoader className="w-8 h-8" />
          </div>
        ) : mustWithhold(palletsQ) ? (
          /* Grid renders empty cells on any state; on a failed/paused fetch that
             reads as "coldroom empty" — a lie. Gate it to an honest error/offline. */
          <QueryStateGate query={palletsQ} noun="coldroom pallets" empty={<div />} />
        ) : (
          <section className="card p-3">
            {bandFocus && (
              <div className="-mx-3 -mt-3 mb-3 overflow-hidden rounded-t-xl">
                <MapFocusPill tone="amber" onShowAll={() => setBandFocus('')}>
                  {/* The count folds bins too: state it only once EVERY read
                      the tiles read is in, as the tiles do — never a pallets-
                      only "No cells at Critical" while bins are in flight. */}
                  {gridRead.state === 'usable'
                    ? bandFocusText(bandFocus, focusedCells, {
                        pallets: overflowPallets.length + strandedPallets.length,
                        bins:    overflowBins.length + strandedBins.length,
                      })
                    : bandFocusPendingText(bandFocus, gridRead.state)}
                </MapFocusPill>
              </div>
            )}
            {/* Row letters and column numbers stay put while the grid pans:
                past the first screen nobody could tell which cell they were
                on — 66% of an 8×20 room hidden at 390 (PM-CST-02, PB-11). Cell
                PADDING, not border-spacing: the gaps a spacing leaves around a
                pinned cell show the panned cells through. */}
            <TableScroll surface="card" maxHeight="max-h-[70vh]">
            <table className="border-collapse [&_td]:p-0.5 [&_th]:p-0.5">
              <thead>
                <tr>
                  <th className="text-caption text-fg-muted font-semibold tracking-wider uppercase pr-2"></th>
                  {cols.map(c => (
                    <th key={c} className="text-caption text-fg-muted font-semibold tracking-wider uppercase text-center min-w-[36px]">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row}>
                    <td className="text-caption text-fg-muted font-semibold tracking-wider uppercase !pr-2 text-right">
                      {row}
                    </td>
                    {cols.map(col => {
                      const key   = `${row}${col}`
                      const cell  = summary.get(key)
                      const palletCount = cell?.palletCount ?? 0
                      const binCount    = cell?.binCount ?? 0
                      // Under a band focus a cell below the band shows as an
                      // empty ghost: no count, no tap (UF-03).
                      const hidden      = !!bandFocus && !cellInBandFocus(cell?.worst ?? null, bandFocus)
                      const total       = hidden ? 0 : palletCount + binCount
                      const colour      = clsx(cellColour(cell?.worst ?? null, total), hidden && 'opacity-40')
                      const hasBins     = binCount > 0
                      // Hidden: no tap (D3). Place only on a cell whose occupancy is READ.
                      const tap         = cellTapAction(hidden, total, gridRead.state === 'usable')
                      const mark        = cellMark(cell?.worst ?? null, total)
                      const titleParts: string[] = []
                      if (palletCount > 0) titleParts.push(`${palletCount} pallet`)
                      if (binCount > 0)    titleParts.push(`${binCount} bin${binCount === 1 ? '' : 's'}`)
                      const title = hidden
                        ? `${key} — hidden by the ${BAND_TILE_LABEL[bandFocus as AgeBand]} tile`
                        : titleParts.length === 0
                        ? `${key} — empty`
                        : `${key} — ${titleParts.join(' + ')}`
                      // Display: pallets show count plain; bins show "Nb"
                      // (lowercase b suffix) so the floor instantly knows what
                      // sits in the cell without clicking through.
                      const display = hidden
                        ? ''
                        : palletCount > 0
                        ? palletCount.toString()
                        : binCount > 0
                          ? `${binCount}b`
                          : ''
                      return (
                        <td key={col}>
                          <button
                            onClick={tap === 'none' ? undefined : () => {
                              if (tap === 'detail') setSelectedCell(key)
                              else setEmptyCell(key)
                            }}
                            aria-disabled={tap === 'none' || undefined}
                            className={clsx(
                              'relative w-9 h-9 coarse:w-11 coarse:h-11 rounded-md border text-caption font-semibold tabular-nums transition-colors',
                              colour,
                              hasBins && palletCount === 0 && !hidden && '!ring-1 !ring-cyan-500/40',
                              tap !== 'none' ? 'cursor-pointer' : 'cursor-default'
                            )}
                            title={mark ? `${title} — ${BAND_MARK_WORD[cell?.worst as AgeBand]}` : title}>
                            {mark && (
                              <span aria-hidden="true" className="absolute top-px left-0.5 text-caption leading-none font-bold">{mark}</span>
                            )}
                            {display}
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            </TableScroll>

            {/* Legend */}
            <div className="flex items-center gap-3 mt-4 text-caption text-fg-muted flex-wrap">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-surface/40 border border-line-subtle" /> Empty
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-emerald-900/70 border border-emerald-700" /> OK
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-amber-900/70 border border-amber-700" /> <b>!</b> Warning
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-red-900/70 border border-red-700" /> <b>!!</b> Critical
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-red-700 border border-red-500" /> <b>!!!</b> Over limit
              </span>
              <span className="ml-auto">Cell number = pallet count. Worst band wins.</span>
            </div>
          </section>
        )}
        </div>

        {/* In the room, on no cell — the overflow, and the fault list beside
            it. Sits directly under the grid because it is the rest of the same
            answer: this is what the map above does not show. */}
        {activeRoom && (
          <div id="coldroom-overflow" className="scroll-mt-24">
          <ColdroomOverflowSection
            roomCode={activeRoom.code}
            pallets={overflowPallets}
            bins={overflowBins}
            strandedPallets={strandedPallets}
            strandedBins={strandedBins}
            flow={flow}
            onPlacePallet={setPlacingPalletId}
            onMoveBins={setOverflowBinIds}
            canOpenBins={canOpenBins}
          />
          </div>
        )}

        {/* Pallets with grid values outside this room's layout */}
        {outOfBounds.length > 0 && activeRoom && (
          <section className="card p-3 border border-warning-edge">
            <p className="text-caption font-semibold text-warning flex items-center gap-1.5 mb-1">
              <AlertTriangle size={12} /> {outOfBounds.length} pallet{outOfBounds.length === 1 ? '' : 's'} outside the {activeRoom.code} layout
            </p>
            <p className="text-caption text-fg-muted mb-2">
              Cells beyond the configured {activeRoom.rows} × {activeRoom.cols} grid. Re-grid the pallet.{' '}
              <SetupPointer target="coldrooms">{link => <>Or enlarge the room in {link}.</>}</SetupPointer>
            </p>
            <div className="space-y-1">
              {outOfBounds.slice(0, 5).map(p => (
                <Link key={p.pallet_id}
                  to={`/pallets/${p.pallet_id}`}
                  className="flex items-center justify-between text-caption hover:bg-surface/50 -mx-1 px-1 py-0.5 rounded-lg">
                  <p className="min-w-0"><PalletIdText id={p.pallet_id} className="text-fg-secondary" /></p>
                  <p className="text-warning font-mono">"{p.coldroom_grid}"</p>
                </Link>
              ))}
              {outOfBounds.length > 5 && (
                <p className="text-caption text-fg-muted italic">… and {outOfBounds.length - 5} more</p>
              )}
            </div>
          </section>
        )}

        {/* Pallets with unparseable grid values — surface so they can be fixed */}
        {unparseable.length > 0 && (
          <section className="card p-3 border border-warning-edge">
            <p className="text-caption font-semibold text-warning flex items-center gap-1.5 mb-1">
              <AlertTriangle size={12} /> {unparseable.length} pallet{unparseable.length === 1 ? '' : 's'} with unparseable grid value
            </p>
            <p className="text-caption text-fg-muted mb-2">Expected format is letter+number (e.g. A1, B12). These pallets aren't shown on the map above.</p>
            <div className="space-y-1">
              {unparseable.slice(0, 5).map(p => (
                <Link key={p.pallet_id}
                  to={`/pallets/${p.pallet_id}`}
                  className="flex items-center justify-between text-caption hover:bg-surface/50 -mx-1 px-1 py-0.5 rounded-lg">
                  <p className="min-w-0"><PalletIdText id={p.pallet_id} className="text-fg-secondary" /></p>
                  <p className="text-warning font-mono">"{p.coldroom_grid}"</p>
                </Link>
              ))}
              {unparseable.length > 5 && (
                <p className="text-caption text-fg-muted italic">… and {unparseable.length - 5} more</p>
              )}
            </div>
          </section>
        )}
        </>
        )}
      </div>
      </SplitPane>
      </div>

      {/* Bin transfer modal — destination cell picker */}
      {showTransfer && (
        <TransferBinsModal
          binIds={Array.from(pickedBinIds)}
          rooms={rooms.filter(r => r.is_active)}
          fromRoomId={effectiveRoomId}
          fromCell={selectedCell}
          onClose={() => setShowTransfer(false)}
          onSuccess={() => {
            setShowTransfer(false)
            setPickedBinIds(new Set())
            setSelectedCell(null)
          }}
        />
      )}

      {/* The same modal, opened from the overflow list instead of a cell
          drawer. fromCell is null because there is no source cell — that is
          what being in the overflow means. */}
      {overflowBinIds && (
        <TransferBinsModal
          binIds={overflowBinIds}
          rooms={rooms.filter(r => r.is_active)}
          fromRoomId={effectiveRoomId}
          fromCell={null}
          onClose={() => setOverflowBinIds(null)}
          onSuccess={() => setOverflowBinIds(null)}
        />
      )}

      {awaitingHit && (
        <PlaceFromProduction hit={awaitingHit} rooms={rooms.filter(r => r.is_active)}
          roomId={effectiveRoomId} onClose={() => setAwaitingHit(null)} />
      )}
      {emptyCell && (
        <PlaceAtCellModal cell={emptyCell} rooms={rooms.filter(r => r.is_active)}
          roomId={effectiveRoomId} onClose={() => setEmptyCell(null)} />
      )}

      {placingPallet && (
        <PlacePalletModal
          pallet={placingPallet}
          rooms={rooms.filter(r => r.is_active)}
          onClose={() => setPlacingPalletId(null)}
          onSuccess={() => setPlacingPalletId(null)}
        />
      )}

      {laneForPallet && (
        <ToLoadingBay key={laneForPallet.pallet_id} pallet={laneForPallet} tenantId={activeTenantId}
          onClose={() => setLaneForPallet(null)} onDone={lane => {
            // UF-01: name the pallet and the lane; close the drawer when the cell is now empty.
            const cell = laneForPallet.coldroom_grid ?? selectedCell
            setSentToBay({ palletId: laneForPallet.pallet_id, lane, cell })
            if (selectedCell && selectedPallets.every(x => x.pallet_id === laneForPallet.pallet_id) && selectedBins.length === 0) {
              setSelectedCell(null); setPickedBinIds(new Set()); setShowTransfer(false)
            }
          }} />
      )}
      {/* Back-date a pallet's coldroom placement date (the aging clock) */}
      {backdatePallet && (
        <BackdatePlacementModal
          palletId={backdatePallet.pallet_id}
          stockedAt={backdatePallet.stocked_at}
          packedAt={backdatePallet.packed_at}
          version={backdatePallet.version}
          onClose={() => setBackdatePallet(null)}
          onSuccess={() => { setBackdatePallet(null); palletsQ.refetch() }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// TransferBinsModal — pick a destination room and either a cell or the room's
// OVERFLOW, then move all selected bins in one atomic RPC
// (transfer_bins_to_cell, mig 660, extended by 20261021100001): per-bin version
// + cell-occupancy checks, whole batch rolls back on any failure (no split
// stacks). Opened from a cell drawer (fromCell set) and from the overflow list
// (fromCell null) — the second is how bins get back onto the grid.
// ---------------------------------------------------------------------------
export function TransferBinsModal({ binIds, rooms, fromRoomId, fromCell, onClose, onSuccess }: {
  binIds:     string[]
  rooms:      Coldroom[]
  fromRoomId: string | null
  fromCell:   string | null
  onClose:    () => void
  onSuccess:  () => void
}) {
  const qc = useQueryClient()
  const [destRoomId, setDestRoomId] = useState<string>(fromRoomId ?? rooms[0]?.coldroom_id ?? '')
  // The destination is a cell OR the room's overflow — never both, which is the
  // same shape the RPC and the CHECK constraint enforce. Holding it as one
  // value rather than a cell string plus a boolean is what makes "both" a state
  // this modal cannot get into.
  const [dest,       setDest]       = useState<ColdroomPlacement | null>(null)
  const [error,      setError]      = useState<string | null>(null)
  const destCell = dest?.kind === 'cell' ? dest.cell : ''
  // A backdrop tap must not discard a picked destination (FCX-04). Modal wires
  // the same useDirtyClose to the backdrop, the X, Cancel AND Escape. Both
  // values seed synchronously (the source room, then blank), so an untouched
  // sheet still closes with one tap.
  const snapshot = `${destRoomId}|${dest?.kind ?? ''}|${destCell}`

  // Pull the bins' current versions in one shot — needed for the
  // optimistic-concurrency check on update.
  const { data: bins = [] } = useQuery({
    queryKey: ['bins-for-transfer', binIds.join(',')],
    enabled: binIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bins')
        .select('bin_id, bin_number, version')
        .in('bin_id', binIds)
      if (error) throw error
      return data as { bin_id: string; bin_number: string; version: number }[]
    },
  })

  const move = useMutation({
    mutationFn: async () => {
      assertTransferDestination(destRoomId, dest, fromRoomId, fromCell)

      // Atomic move (mig 660): all selected bins move in ONE transaction. The
      // old code looped updateWithVersion per bin — a mid-loop failure (stale
      // version, or the cell-occupancy trigger rejecting an overflow) committed
      // the bins that already landed and stranded the rest, splitting a stack
      // across two cells. The RPC enforces per-bin version + cell-occupancy and
      // rolls the whole batch back on any failure, so the move is all-or-nothing.
      //
      // p_overflow is the destination KIND, and p_coldroom_grid must be null
      // when it is true — the RPC refuses a caller that names both rather than
      // guessing which was meant (mig 20261021100001).
      const { error } = await withTimeout(supabase.rpc('transfer_bins_to_cell', {
        p_bin_ids:           bins.map(b => b.bin_id),
        p_expected_versions: bins.map(b => b.version),
        p_coldroom_id:       destRoomId,
        p_coldroom_grid:     dest.kind === 'cell' ? dest.cell : null,
        p_overflow:          dest.kind === 'overflow',
      }), 'Coldroom: transfer_bins_to_cell')
      if (error) throw transferBinsError(error)
    },
    onSuccess: () => {
      invalidateColdroomViews(qc)
      onSuccess()
    },
    onError: (e: Error) => setError(e.message),
  })

  // fromCell is null when these bins came off the OVERFLOW list rather than out
  // of a cell drawer — "—" would read as "we don't know", so name the place.
  const fromRoomCode = rooms.find(r => r.coldroom_id === fromRoomId)?.code
  const binCount = `${bins.length} bin${bins.length === 1 ? '' : 's'}`
  const sourceLabel = transferSourceLabel(fromRoomCode, fromCell)

  return (
    <Modal
      title={
        <span className="flex items-center gap-2">
          <Snowflake size={16} className="text-cyan-400 shrink-0" />
          <span className="truncate">Move {binCount}</span>
        </span>
      }
      onClose={onClose}
      snapshot={snapshot}
      onSubmit={() => move.mutate()}
      isPending={move.isPending}
      submitDisabled={!destRoomId || !placementIsComplete(dest)}
      submitLabel={`Move ${binCount} to ${placementLabel(dest)}`}
      submitPendingLabel="Moving…">
        <div className="space-y-3">
          <div className="bg-surface/40 rounded-lg p-3 text-caption">
            <p className="text-fg-muted uppercase tracking-wider mb-1">From</p>
            <p className="font-mono text-cyan-300">{sourceLabel}</p>
          </div>

          <DestinationRoomTabs rooms={rooms} value={destRoomId}
            onPick={id => { setDestRoomId(id); setDest(null) }} />

          <div>
            <p className="text-caption uppercase tracking-wider text-fg-muted mb-1.5">Destination</p>
            <CellPicker
              mode="bin"
              coldroomId={destRoomId || null}
              value={destCell}
              onChange={cell => setDest({ kind: 'cell', cell })}
              allowOverflow
              overflowPicked={dest?.kind === 'overflow'}
              onPickOverflow={() => setDest({ kind: 'overflow' })}
            />
          </div>

          {error && (
            <div className="rounded-lg border border-danger-edge bg-danger-soft p-3 text-caption text-danger whitespace-pre-line">
              {error}
            </div>
          )}

          <p className="text-caption text-fg-muted text-center">
            Stack height limits will apply at the destination.
          </p>
        </div>
    </Modal>
  )
}

// `value` is a string, not a number: the caller has already decided whether it
// is allowed to state this figure at all (see `stat` on the page). A tile that
// takes a number can only ever render one.
/** Below sm: a toggle, with its children folded until it is opened. From sm
 *  the children are laid out exactly as before (`display: contents` keeps them
 *  flex items of the page column), and the toggle is gone. */
// A drawer bin row: opens the bin for a viewer who may open bins, the same
// card as text for one without (F05 UF-05).
function BinRowShell({ to, returnTo, children }: { to: string | null; returnTo: string; children: ReactNode }) {
  return to
    ? <Link to={to} state={{ returnTo }} className="flex-1 min-w-0 block hover:bg-raised rounded-lg p-2.5">{children}</Link>
    : <div className="flex-1 min-w-0 p-2.5">{children}</div>
}

// A card that opens the Force-Cooling board, or the same card as text (no
// chevron) for a viewer the route would refuse (UF-05 rule, UF-06 cards).
function PrecoolCard({ canOpen, className, hoverClass, chevronClass, children }: {
  canOpen: boolean; className: string; hoverClass: string; chevronClass: string; children: ReactNode
}) {
  const base = clsx('card p-3 flex items-center gap-2.5 border', className)
  return canOpen
    ? (
      <Link to="/precool" className={clsx(base, hoverClass)}>
        {children}
        <ChevronRight size={15} className={clsx(chevronClass, 'shrink-0')} />
      </Link>
    )
    : <div className={base}>{children}</div>
}

function PhoneFold({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="sm:hidden min-h-[44px] w-full card py-2 px-3 flex items-center gap-2 text-caption font-semibold text-fg-secondary">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {label}
      </button>
      <div className={clsx(open ? 'contents' : 'hidden sm:contents')}>{children}</div>
    </>
  )
}

const STAT_TONE: Record<AgeBand, string> = {
  ok:       'bg-emerald-900/40 text-emerald-300 border-emerald-800',
  warning:  'bg-amber-900/40 text-amber-300 border-amber-800',
  critical: 'bg-red-900/40 text-red-300 border-red-800',
  over:     'bg-red-700/60 light:bg-red-700 text-red-100 light:text-on-fill border-red-500',
}

// A band tile is a button: it opens what it counts (F05 UF-03). The lit tile
// carries a white ring and aria-pressed; tapping it again shows the room.
function Stat({ label, value, tone, selected, onClick }: {
  label: string; value: string; tone: string; selected: boolean; onClick: () => void
}) {
  return (
    <button type="button" onClick={onClick} aria-pressed={selected}
      aria-label={`${label}: ${value}. ${selected ? 'Showing only these cells — tap to show the whole room' : 'Show only these cells'}`}
      className={clsx('min-h-[44px] w-full text-left rounded-xl border px-3 py-2', tone,
        selected && 'ring-2 ring-white/80')}>
      <p className="text-caption uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-lg font-bold tabular-nums">{value}</p>
    </button>
  )
}

// CapacityRow — slim "X of Y" with a horizontal bar. Tone is set by % full
// so a nearly-full coldroom turns amber/red without the operator hunting.
function CapacityRow({ label, remaining, total, used, pctUsed, colour, hint }: {
  label:     string
  remaining: number
  total:     number
  used:      number
  pctUsed:   number
  colour:    string
  hint:      string
}) {
  const tone =
    pctUsed >= 95 ? 'text-red-400'    :
    pctUsed >= 80 ? 'text-amber-400'  :
                    'text-emerald-400'
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <p className="text-caption text-fg-muted">{label}</p>
        <p className={clsx('text-sm font-bold tabular-nums', tone)}>
          {remaining}
          <span className="text-caption text-fg-muted font-normal ml-1">free</span>
        </p>
      </div>
      <div className="h-2 rounded-full bg-surface overflow-hidden mt-1">
        <div className={clsx('h-full transition-[width]', colour)} style={{ width: `${pctUsed}%` }} />
      </div>
      <p className="text-caption text-fg-muted mt-1 flex items-center justify-between">
        <span>{hint}</span>
        <span className="tabular-nums">{used} / {total} · {pctUsed}%</span>
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Cold-storage accrued — what the pallets currently in this room have accrued
// so far. Pulls cold_storage_revenue (mig 20261035100000: each storage
// client's pallet carries its share of the room-days it stood in x the rate,
// R per coldroom per day; own and packed-here fruit carries 0), filters to
// pallets still in cold (dispatched_at IS NULL), and sums revenue. Hidden if
// rate is 0. The line under the title says that rule (F07-FLOOR:WORDS): it
// used to read "N pallets · X pallet-days @ R/day", which invites days x rate.
// ---------------------------------------------------------------------------
function ColdStorageAccrued({ coldroomId }: { coldroomId: string | null }) {
  const { money } = useMoney()
  const { data } = useQuery({
    queryKey: ['cold-storage-accrued', coldroomId],
    enabled: !!coldroomId,
    refetchInterval: POLL.standard,
    queryFn: async () => {
      const { data: revRows, error } = await supabase.from('cold_storage_revenue')
        // No days_in_cold: the card no longer states it, and the view only
        // works it out (per pallet, from its history) for a query that reads it.
        .select('pallet_id, rate_per_day, revenue, dispatched_at')
        .is('dispatched_at', null)
        .range(0, 49_999)
      if (error) throw error
      const { data: palletRows, error: pErr } = await supabase.from('pallets')
        .select('pallet_id, coldroom_id, status')
        .eq('coldroom_id', coldroomId!)
        .is('deleted_at', null)
        .range(0, 49_999)
      if (pErr) throw pErr
      // U18 D1: pallets that left keep coldroom_id and still accrue until
      // dispatched, so the one figure is split, not filtered (total unchanged).
      return splitRentalByStatus(
        (revRows ?? []) as unknown as RevenueRow[],
        (palletRows ?? []) as unknown as RoomPalletRow[],
      )
    },
  })

  if (!data || data.rate === 0 || data.pallets === 0) return null

  return (
    <section className="card p-3 sm:p-4 border border-cyan-900/60 bg-cyan-950/20">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-caption font-semibold uppercase tracking-wider text-cyan-300 flex items-center gap-1.5">
            <Snowflake size={12} /> Cold-storage rental accruing
          </p>
          <p className="text-caption text-fg-muted mt-0.5">
            {coldroomAccruingLine(money(data.rate))}
          </p>
        </div>
        <div className="text-right tabular-nums">
          <p className="text-caption text-fg-muted">
            In the room now{' '}
            <span className="text-cyan-300 font-bold text-xl"><Money value={data.inRoomRevenue} /></span>
          </p>
          <p className="text-caption text-fg-muted">
            Left, not yet billed{' '}
            <span className="text-cyan-300 font-bold text-base"><Money value={data.leftRevenue} /></span>
          </p>
        </div>
      </div>
    </section>
  )
}
